// API Configuration
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Access token lives in module-scoped memory only — never localStorage.
// Reasoning: localStorage is readable by any injected script, so an XSS would
// hand over the user's session. In-memory tokens die with the tab; the refresh
// token lives in an httpOnly cookie that JS can't read.
let _accessToken = null;
const _accessTokenListeners = new Set();

export const setAccessToken = (token) => {
  _accessToken = token || null;
  _accessTokenListeners.forEach((fn) => {
    try { fn(_accessToken); } catch (_) { /* noop */ }
  });
};
export const getAccessToken = () => _accessToken;
export const clearAccessToken = () => setAccessToken(null);
export const onAccessTokenChange = (fn) => {
  _accessTokenListeners.add(fn);
  return () => _accessTokenListeners.delete(fn);
};

// "View as" preview — while an admin previews another user/role, every data
// request carries these headers so the BACKEND authorizes and filters as the
// target (see chariot-backend/middleware/viewAs.js). Set/cleared by
// AuthContext's preview controls. Identity calls (/auth/me login path,
// /auth/refresh) build bare Authorization headers directly, so the admin's
// own session is never affected.
let _viewAs = null;
export const setViewAsRequestHeaders = (viewAs) => {
  _viewAs = viewAs || null;
};

// Helper to get auth headers
export const getAuthHeaders = () => {
  const headers = _accessToken
    ? { 'Authorization': `Bearer ${_accessToken}` }
    : {};
  if (_viewAs?.userId) headers['X-View-As-User'] = _viewAs.userId;
  else if (_viewAs?.role) headers['X-View-As-Role'] = _viewAs.role;
  return headers;
};

// Custom header that gates the cookie-authenticated /auth/refresh endpoint.
// Browsers won't let cross-origin attackers set this without a CORS preflight,
// so requiring it blocks CSRF against the only cookie-auth'd route we expose.
export const XHR_HEADER = { 'X-Requested-With': 'chariot-web' };

// Refresh the access token using the httpOnly refresh cookie.
// Mutex: if multiple 401s fire at once, only one refresh runs and
// all callers share the same result. Prevents refresh-token-rotation
// race conditions that would invalidate tokens for concurrent requests.
//
// A failed refresh records WHY it failed: 'unauthorized' (the cookie is
// really invalid/expired — the user is logged out) vs 'unavailable' (the
// server was slow/unreachable/5xx — the SESSION IS STILL VALID and callers
// must retry, never bounce the user to the login page). Losing that
// distinction was how a Cloud Run stall used to masquerade as a logout.
let _refreshPromise = null;
let _refreshFailure = null; // null | 'unauthorized' | 'unavailable'
export const getRefreshFailure = () => _refreshFailure;
// The last refresh got no answer at all (no signal, or none in 12 s) — as
// opposed to Chariot answering with trouble. AuthContext opens a technician's
// kept session on it.
let _refreshNoAnswer = false;
export const refreshGotNoAnswer = () => _refreshNoAnswer;
// Refresh tokens rotate on every use and the previous one is revoked, so two
// tabs (or a browser restoring several) POSTing /refresh with the same cookie
// milliseconds apart made the loser trip reuse-detection and log EVERY tab
// out (Sep 2026 audit). The per-tab single-flight above can't see other
// tabs; a browser-wide Web Lock can: the second tab waits, then refreshes
// with the cookie the first tab just received. Browsers without Web Locks
// (none we support) fall back to per-tab behaviour.
const withCrossTabRefreshLock = (fn) =>
  typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request('chariot-auth-refresh', fn)
    : fn();

export const refreshAccessToken = () => {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = withCrossTabRefreshLock(async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    _refreshNoAnswer = false;
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...XHR_HEADER },
        credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          _refreshFailure = 'unauthorized';
          clearAccessToken();
        } else {
          _refreshFailure = 'unavailable'; // 5xx / 429 — server-side trouble
        }
        return false;
      }

      const data = await response.json();
      if (data?.accessToken) {
        _refreshFailure = null;
        setAccessToken(data.accessToken);
        return true;
      }
      _refreshFailure = 'unavailable';
      return false;
    } catch (err) {
      // Timeout or network error — the server never answered; the cookie
      // is untouched. Treat as transient.
      console.error('Token refresh failed (transient):', err);
      _refreshFailure = 'unavailable';
      _refreshNoAnswer = true;
      return false;
    } finally {
      clearTimeout(timer);
    }
  }).finally(() => { _refreshPromise = null; });

  return _refreshPromise;
};

// Default request timeout (ms). Prevents the UI from hanging indefinitely
// if Cloud Run is slow or unreachable. Callers can override via options.timeout.
const DEFAULT_TIMEOUT_MS = 30000;

// Generic fetch with auth and automatic token refresh on 401
// Methods that count as mutations for the save-indicator pulse.
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// The global SaveIndicator in the top bar: a spinner while saves are in
// flight, then a green check only if nothing is left unsaved, or a red
// "Not saved" — a failed save must never look saved (a tech on weak marina
// signal has to know). Each save is tracked on its own, so overlapping saves
// settle together, and ends one of three ways:
//   'ok'      saved.
//   'lost'    no answer, timed out, or a server error: it stays red until a
//             save of the same thing STARTED AFTER it works (an older save
//             answering OK late never clears it), or the person dismisses
//             the pill. Another save working never paints it green.
//   'refused' the server said no and why (4xx; the caller shows the reason):
//             red until the next save, as it always was.
// Mutating fetchWithAuth calls (with their own timeout) and raw fetches to
// order paths report automatically; anything else calls
// `const finish = triggerSaveIndicator.begin(key)` and later `finish(ok)`
// (true = 'ok', false = 'lost').
const RAW_SAVE_WATCHDOG_MS = 45000; // a raw fetch has no timeout of its own
const pendingSaves = new Map(); // save id → its key
const lostSaves = new Map(); // key → id of its newest lost save
const okSaves = new Map(); // key → id of its newest save that worked (while others of it are in flight)
let refusedSinceSettle = false;
let nextSaveId = 1;
const outcomeOfStatus = (status, okStatuses = []) => {
  if ((status >= 200 && status < 300) || okStatuses.includes(status)) return 'ok';
  return status >= 400 && status < 500 && status !== 408 && status !== 429 ? 'refused' : 'lost';
};
// Which save this is: the same method, path and fields sent — so editing the
// same field again and succeeding clears its red, while another field saving
// on the same order doesn't. The same whether it went through fetchWithAuth
// ('/orders/…') or a raw fetch ('http://…/api/orders/…').
const saveKeyOf = (method, url, body) => {
  let fields = '';
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) fields = Object.keys(parsed).sort().join(',');
    } catch (e) { /* not JSON */ }
  }
  const path = String(url || '').replace(/^https?:\/\/[^/]+/, '').replace(/^\/api(?=\/)/, '').split('?')[0];
  return `${method} ${path} ${fields}`;
};
const settleIfIdle = () => {
  if (pendingSaves.size > 0) return;
  const failed = lostSaves.size > 0 || refusedSinceSettle;
  refusedSinceSettle = false;
  const ui = window.__chariotSaveIndicator;
  try { if (failed) ui?.triggerFailed?.(); else ui?.triggerSaved?.(); } catch (e) { /* noop */ }
};
// Start tracking one save; returns finish(outcome). With a watchdog, a save
// that doesn't answer in time counts as lost — and if it does answer OK
// later (and nothing newer of it failed since), that clears it.
const beginSave = (key, watchdogMs) => {
  const id = nextSaveId++;
  pendingSaves.set(id, key);
  try { window.__chariotSaveIndicator?.triggerSaving(); } catch (e) { /* noop */ }
  let state = 'pending';
  const markLost = () => {
    if ((okSaves.get(key) || 0) > id) return; // a newer save of the same thing worked
    if ((lostSaves.get(key) || 0) < id) lostSaves.set(key, id);
  };
  const timer = watchdogMs ? setTimeout(() => {
    state = 'expired';
    pendingSaves.delete(id);
    markLost();
    settleIfIdle();
  }, watchdogMs) : null;
  return (outcome) => {
    if (state === 'done') return;
    const late = state === 'expired';
    state = 'done';
    clearTimeout(timer);
    pendingSaves.delete(id);
    const result = outcome === true ? 'ok' : outcome === false ? 'lost' : outcome;
    if (result === 'ok') {
      if ((okSaves.get(key) || 0) < id) okSaves.set(key, id);
      if (lostSaves.has(key) && lostSaves.get(key) <= id) lostSaves.delete(key);
    } else if (result === 'refused' && !late) {
      refusedSinceSettle = true;
    } else {
      markLost();
    }
    // Only needed while other saves of the same thing are still out.
    if (![...pendingSaves.values()].includes(key)) okSaves.delete(key);
    if (!late || result === 'ok') settleIfIdle();
  };
};
// How long since this tab last sent a change (any POST/PUT/PATCH/DELETE,
// stamped as it goes and as it's answered, by the fetch hook below): the
// order page tells the echo of its own save (order:updated) from a change the
// same person made in another tab or on another device.
export const msSinceLastWrite = () => Date.now() - ((typeof window !== 'undefined' && window.__chariotLastWriteAt) || 0);

export const triggerSaveIndicator = {
  begin: (key = `save ${nextSaveId}`) => beginSave(key, RAW_SAVE_WATCHDOG_MS),
  // The person dismissed the red pill: they've seen it.
  dismissFailures: () => { lostSaves.clear(); refusedSinceSettle = false; },
};

export const fetchWithAuth = async (endpoint, options = {}) => {
  const timeoutMs = options.timeout ?? DEFAULT_TIMEOUT_MS;
  const method = (options.method || 'GET').toUpperCase();
  const isMutation = MUTATION_METHODS.has(method) && options.skipSaveIndicator !== true;

  // FormData bodies must NOT have an explicit Content-Type — the browser sets
  // it (with the correct multipart boundary) only when the header is absent.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const makeRequest = async () => {
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...getAuthHeaders(),
      ...options.headers
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include',
        signal: options.signal || controller.signal,
        // The global fetch hook below skips this request — it is reported
        // here, once, with its final answer (after any token refresh).
        __chariotTracked: true,
      });
    } finally {
      clearTimeout(timer);
    }
  };

  // Settle the SaveIndicator for mutations with whether it really saved: a
  // timeout (this request's own), a network error or an error status is
  // "Not saved" — except statuses the caller expects as done (`okStatuses`,
  // e.g. 409 "already on the list").
  const finishSave = isMutation ? beginSave(saveKeyOf(method, endpoint, options.body), 0) : null;
  const okStatuses = options.okStatuses || [];
  let finalResponse = null;
  // No final response = no answer or this request's own timeout: lost.
  const ensureDone = () => finishSave?.(finalResponse ? outcomeOfStatus(finalResponse.status, okStatuses) : 'lost');

  try {
    let response;
    try {
      response = await makeRequest();
    } catch (err) {
      if (err.name === 'AbortError') {
        // Return a synthetic 504 so callers can treat this like any bad response
        return new Response(JSON.stringify({ error: 'Request timed out' }), {
          status: 504,
          headers: { 'Content-Type': 'application/json' }
        });
      }
      throw err;
    }

    // If token expired (401) OR the server rejected the token as invalid
    // (403 "Invalid token" — happens on JWT secret rotation across Cloud Run
    // instances, clock skew, or other non-expiry decode failures), try a
    // single refresh + retry. We avoid retrying on plain 403 permission
    // denials by sniffing the body first.
    let shouldRetryAuth = response.status === 401;
    if (!shouldRetryAuth && response.status === 403) {
      try {
        // Clone so the caller can still .json()/.text() the original response
        // if we end up not retrying.
        const peek = await response.clone().json();
        if (peek?.error === 'Invalid token') shouldRetryAuth = true;
      } catch (_) { /* not JSON or no body — leave shouldRetryAuth false */ }
    }
    if (shouldRetryAuth) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        try {
          response = await makeRequest();
        } catch (err) {
          if (err.name === 'AbortError') {
            return new Response(JSON.stringify({ error: 'Request timed out' }), {
              status: 504,
              headers: { 'Content-Type': 'application/json' }
            });
          }
          throw err;
        }
      }
    }

    finalResponse = response;
    return response;
  } finally {
    ensureDone();
  }
};

// ─── Global fetch interceptor for the SaveIndicator ─────────────────────────
//
// Many older components in the order detail tree call window.fetch directly
// (rather than fetchWithAuth) to do PUT/POST/DELETE. We can't migrate them all
// at once without risk. Instead, hook the global fetch and, for mutating
// requests against order-affecting API paths, report them to the
// SaveIndicator — saved only if the server answered OK.
//
// Scope: only methods POST/PUT/PATCH/DELETE, only paths that affect order
// state, and not fetchWithAuth's own requests (it reports those itself).
// Never alters the request or response — it only reads the status.
if (typeof window !== 'undefined' && !window.__chariotFetchPatched) {
  const ORDER_PATH_RE = /\/api\/(?:admin\/)?(?:orders|services|inspections|timeclocks|appointments|customers|vehicles|vehicle-owners|inventory-(?:parts|labor|subcontracts|fees)|payments|engine-profiles|labor-rates|tax-config|transactions|labels|messages|notes|v1\/orders)\b/;
  // Order-path POSTs that save nothing the person typed: an AI suggestion
  // for a note or a photo, and marking messages read. They don't count.
  const NOT_A_SAVE_RE = /\/ai\/(?:refine-note|analyze-photo)|\/read(?:[/?]|$)/;
  const _origFetch = window.fetch.bind(window);
  const stampWrite = () => { window.__chariotLastWriteAt = Date.now(); };
  window.fetch = function patchedFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = (init.method || (input && input.method) || 'GET').toUpperCase();
    const isMut = MUTATION_METHODS.has(method);
    const isSave = isMut && ORDER_PATH_RE.test(url) && !NOT_A_SAVE_RE.test(url) && !(init && init.__chariotTracked);
    const finish = isSave ? beginSave(saveKeyOf(method, url, init && init.body), RAW_SAVE_WATCHDOG_MS) : null;
    const promise = _origFetch(input, init);
    if (finish) promise.then((res) => finish(outcomeOfStatus(res.status)), () => finish('lost'));
    if (isMut) { stampWrite(); promise.then(stampWrite, stampWrite); }
    return promise;
  };
  window.__chariotFetchPatched = true;
}

/**
 * Read a JSON body defensively.
 *
 * `await res.json()` on a response with an empty body throws a raw
 * "JSON.parse: unexpected end of data at line 1 column 1" — a message that
 * tells the person reading it nothing about what actually failed. Any handler
 * that surfaces `err.message` to the user should read bodies through this so
 * they get `context` instead.
 *
 * @param {Response} res
 * @param {string} context  what was being done, e.g. "Couldn't combine the shapes"
 * @returns {Promise<any>} the parsed body
 * @throws {Error} with `context` when the body is empty or not JSON
 */
export const readJsonOrThrow = async (res, context) => {
  const text = await res.text();
  if (!text.trim()) {
    throw new Error(`${context} — the server returned an empty response.`);
  }
  try {
    return JSON.parse(text);
  } catch (_) {
    throw new Error(`${context} — the server returned an unexpected response.`);
  }
};

/** Same, but for error branches: returns {} instead of throwing. */
export const readJsonSafe = async (res) => {
  try {
    const text = await res.text();
    return text.trim() ? JSON.parse(text) : {};
  } catch (_) {
    return {};
  }
};
